import { t } from "@lingui/macro"
import {
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Switch,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useEffect } from "react"

import { GlobalStore } from "src/store"

import { DateTimePicker } from "src/components/DateTimePicker"
import { FormFieldSet } from "src/components/FormFieldSet"
import { SegmentPickerField } from "src/components/SegmentPickerField"
import { NoticeBoardPostDetailStore } from "src/modals/notice-board-post-detail/store"
import { useStore } from "src/store/lib/useStore"

interface IProps {
    mode?: string
}

export const PublishSettingsSection = observer((props: IProps) => {
    const store = useStore(NoticeBoardPostDetailStore)
    const globalStore = useStore(GlobalStore)
    useEffect(() => {
        props.mode === "Copy" && store.fields.set("segment_ids", [])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.mode])

    const handleIncludeInChatbotChange = useCallback(
        (_: unknown, checked: boolean) => {
            store.fields.set("include_in_chatbot", checked)
        },
        [store.fields],
    )

    const handleSegmentsChange = useCallback(
        (segments: number[]) => {
            store.fields.set("segment_ids", segments)
        },
        [store.fields],
    )

    const handleDimensionIdsChange = useCallback(
        (dimensionIds: number[]) => {
            store.fields.set("dimensionIds", dimensionIds)
        },
        [store.fields],
    )

    return (
        <>
            <FormFieldSet
                header={t`notice-board-post-detail-modal.publish-settings-section.receivers-header`}
                tooltip={t`notice-board-post-detail-modal.publish-settings-section.receivers-tooltip`}
            >
                <SegmentPickerField
                    value={store.fields.get("segment_ids")}
                    accessGroupId={store.fields.get("accessGroupId")}
                    onChange={handleSegmentsChange}
                    disabled={store.fields.get("access_type") === "READ"}
                    dimensionIds={store.fields.get("dimensionIds")}
                    onPublishGroupChange={handleDimensionIdsChange}
                />
            </FormFieldSet>
            <FormFieldSet
                header={t`notice-board-post-detail-modal.publish-settings-section.publish-date-header`}
                tooltip={t`notice-board-post-detail-modal.publish-settings-section.publish-date-tooltip`}
            >
                <Stack
                    spacing={2}
                    direction={{
                        lg: "row",
                        md: "row",
                        sm: "row",
                        xs: "column",
                    }}
                >
                    <DateTimePicker
                        value={store.fields.get("publish_at")}
                        onChangePicker={(date: Date | null) => {
                            store.fields.set("publish_at", date)
                        }}
                        label={t`notice-board-post-detail-modal.publish-settings-section.publish-start-label`}
                        maxDate={store.fields.get("publish_at")}
                        inputId="publish-at"
                        disabled={store.fields.get("access_type") === "READ"}
                    />
                    <DateTimePicker
                        value={store.fields.get("unpublish_at")}
                        onChangePicker={(date: Date | null) => {
                            store.fields.set("unpublish_at", date)
                        }}
                        label={t`notice-board-post-detail-modal.publish-settings-section.publish-end-label`}
                        minDate={store.fields.get("unpublish_at")}
                        inputId="un-publish-at"
                        disabled={store.fields.get("access_type") === "READ"}
                    />
                </Stack>
            </FormFieldSet>
            <FormFieldSet
                header={t`notice-board-post-detail-modal.publish-settings-section.pinned-post-header`}
                tooltip={t`notice-board-post-detail-modal.publish-settings-section.pinned-post-tooltip`}
            >
                <RadioGroup
                    aria-label="hello"
                    value={store.fields.get("pinned")}
                    onChange={(_, value) => {
                        store.fields.set("pinned", value === "true")
                    }}
                >
                    <Stack direction="row" spacing={1}>
                        <FormControlLabel
                            label={t`notice-board-post-detail-modal.publish-settings-section.pinned-post-yes`}
                            value={true}
                            disabled={
                                store.fields.get("access_type") === "READ"
                            }
                            control={<Radio color="info" />}
                        />
                        <FormControlLabel
                            label={t`notice-board-post-detail-modal.publish-settings-section.pinned-post-no`}
                            value={false}
                            disabled={
                                store.fields.get("access_type") === "READ"
                            }
                            control={<Radio color="info" />}
                        />
                    </Stack>
                </RadioGroup>
            </FormFieldSet>
            <FormFieldSet
                header={t`notice-board-post-detail-modal.publish-settings-section.push-notificaiton-header`}
                description={t`notice-board-post-detail-modal.publish-settings-section.push-notification-description`}
            >
                <RadioGroup
                    value={store.fields.get("send_push")}
                    onChange={(_, value) => {
                        store.fields.set("send_push", value === "true")
                    }}
                >
                    <FormControlLabel
                        label={t`notice-board-post-detail-modal.publish-settings-section.push-notificaiton-no`}
                        value={false}
                        disabled={store.fields.get("access_type") === "READ"}
                        control={<Radio color="info" />}
                    />
                    <FormControlLabel
                        label={t`notice-board-post-detail-modal.publish-settings-section.push-notificaiton-yes`}
                        value={true}
                        disabled={store.fields.get("access_type") === "READ"}
                        control={<Radio color="info" />}
                    />
                </RadioGroup>
            </FormFieldSet>
            {globalStore.session.hasAccessToModule("ai_chatbot") && (
                <>
                    <FormControlLabel
                        checked={store.fields.get("include_in_chatbot")}
                        onChange={handleIncludeInChatbotChange}
                        control={<Switch color="success" />}
                        label={t`edit-information-item-modal.include-in-chat-bot-label`}
                        sx={{ ml: 1, mt: 2 }}
                    />
                </>
            )}
        </>
    )
})
